import React, { useState, useEffect } from 'react';
import { 
  Scale,
  Clock, 
  Users, 
  CheckCircle,
  AlertCircle,
  Camera,
  CameraOff,
  Mic,
  MicOff,
  Upload,
  FileText,
  Phone,
  MessageCircle,
  Shield,
  Monitor,
  Loader,
  Video
} from 'lucide-react';
import { BrandingProvider } from '../../contexts/BrandingContext';
import { 
  BrandedHeader, 
  BrandedButton, 
  BrandedCard, 
  BrandedInput, 
  BrandedSelect, 
  BrandedTextarea,
  BrandedBadge,
  BrandedAlert 
} from '../../components/branding/BrandedComponents';

interface ClientWaitingRoomProps {
  firmName?: string;
  firmLogo?: string;
  organizationId?: string; // For API calls
}

interface ClientInfo {
  name: string;
  email: string;
  phone: string;
  caseType: string;
  urgency: 'high' | 'medium' | 'low';
  description: string;
  preferredLanguage: string;
}

interface ConsultationStatus {
  requestId: string;
  status: 'waiting' | 'accepted' | 'ready' | 'expired';
  clientName: string;
  firmName: string;
  caseType: string;
  urgency: string;
  createdAt: string;
  expiresAt: string;
  queuePosition?: number;
  estimatedWait?: string;
  meetingId?: string;
  roomId?: string;
}

export const ClientWaitingRoom: React.FC<ClientWaitingRoomProps> = ({
  firmName = 'Smith & Associates Law Firm',
  firmLogo,
  organizationId
}) => {
  // Require organizationId to be provided
  if (!organizationId) {
    console.error('🔴 CLIENT: organizationId is required for ClientWaitingRoom');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Configuration Error</h2>
          <p className="text-gray-600">Organization ID is missing. Please check the URL.</p>
        </div>
      </div>
    );
  }
  
  return (
    <BrandingProvider templateType="legal" initialBranding={{ companyName: firmName, companyLogo: firmLogo }}>
      <ClientWaitingRoomContent 
        firmName={firmName}
        firmLogo={firmLogo}
        organizationId={organizationId}
      />
    </BrandingProvider>
  );
};

const ClientWaitingRoomContent: React.FC<ClientWaitingRoomProps> = ({
  firmName = 'Smith & Associates Law Firm',
  firmLogo,
  organizationId
}) => {
  const [step, setStep] = useState<'intake' | 'waiting' | 'ready' | 'error'>('intake');
  const [clientInfo, setClientInfo] = useState<ClientInfo>({
    name: '',
    email: '',
    phone: '',
    caseType: '',
    urgency: 'medium',
    description: '',
    preferredLanguage: 'en'
  });
  const [consultationStatus, setConsultationStatus] = useState<ConsultationStatus | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);

  // Available case types
  const caseTypes = [
    'personal_injury',
    'criminal_defense', 
    'family_law',
    'contract_dispute',
    'employment_law',
    'estate_planning',
    'business_law',
    'immigration',
    'other'
  ];

  // Submit consultation request
  const submitConsultationRequest = async () => {
    // Validate required fields
    if (!clientInfo.name.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!clientInfo.email.trim()) {
      setError('Please enter your email address');
      return;
    }
    if (!clientInfo.phone.trim()) {
      setError('Please enter your phone number');
      return;
    }
    if (!clientInfo.caseType) {
      setError('Please select a case type');
      return;
    }
    if (!clientInfo.description.trim()) {
      setError('Please provide a brief description of your case');
      return;
    }

    const requestPayload = {
      organizationId,
      clientName: clientInfo.name,
      firmName,
      caseType: clientInfo.caseType,
      urgency: clientInfo.urgency,
      metadata: {
        phone: clientInfo.phone,
        email: clientInfo.email,
        caseDescription: clientInfo.description,
        preferredLanguage: clientInfo.preferredLanguage,
        templateType: 'legal'
      }
    };

    console.log('🔵 CLIENT: Submitting consultation request:', requestPayload);

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch('https://dev-platform-api-dev.benjiemalinao879557.workers.dev/client/v1/consultation-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestPayload)
      });

      console.log('🔵 CLIENT: API Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔴 CLIENT: API Error response:', errorText);
        throw new Error(`Failed to submit request: ${response.status}`);
      }

      const data = await response.json();
      console.log('🟢 CLIENT: Consultation request submitted successfully:', data);
      console.log('🆔 CLIENT: Request ID generated:', data.requestId);
      if (data.meetingId) {
        console.log('🎥 CLIENT: Meeting ID:', data.meetingId);
      }
      
      setRequestId(data.requestId);
      setStep('waiting');
      
      // Set initial consultation status with meeting ID
      if (data.meetingId) {
        setConsultationStatus({
          requestId: data.requestId,
          status: 'waiting',
          clientName: clientInfo.name,
          firmName: firmName,
          caseType: 'personal_injury',
          urgency: 'high',
          createdAt: new Date().toISOString(),
          expiresAt: data.expiresAt,
          queuePosition: data.queuePosition,
          estimatedWait: data.estimatedWait,
          meetingId: data.meetingId
        });
      }
      
      // Start polling for status updates
      startStatusPolling(data.requestId);

    } catch (err) {
      console.error('🔴 CLIENT: Error submitting consultation request:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit consultation request');
      setStep('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Poll for consultation status updates
  const startStatusPolling = (reqId: string) => {
    console.log('🔄 CLIENT: Starting status polling for request ID:', reqId);
    
    const pollStatus = async () => {
      try {
        console.log('🔍 CLIENT: Polling status for request ID:', reqId);
        const response = await fetch(`https://dev-platform-api-dev.benjiemalinao879557.workers.dev/client/v1/consultation-status/${reqId}`);
        
        if (response.ok) {
          const statusData = await response.json();
          console.log('📊 CLIENT: Status update received:', statusData);
          setConsultationStatus(statusData);
          
          // Check if consultation is ready
          if (statusData.status === 'accepted' || statusData.status === 'ready') {
            console.log('✅ CLIENT: Consultation accepted! Attempting to join...');
            // Try to join the consultation
            await tryJoinConsultation(reqId);
          }
        } else {
          console.warn('⚠️ CLIENT: Status polling failed with status:', response.status);
        }
      } catch (err) {
        console.error('🔴 CLIENT: Error polling status:', err);
      }
    };

    // Poll every 5 seconds
    const interval = setInterval(pollStatus, 5000);
    
    // Clean up interval after 1 hour
    setTimeout(() => clearInterval(interval), 60 * 60 * 1000);
    
    // Initial poll
    pollStatus();
  };

  // Try to join the consultation
  const tryJoinConsultation = async (reqId: string) => {
    try {
      console.log('🚪 CLIENT: Attempting to join consultation:', reqId);
      const response = await fetch(`https://dev-platform-api-dev.benjiemalinao879557.workers.dev/client/v1/join/${reqId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          participantName: clientInfo.name,
          role: 'participant'
        })
      });

      console.log('🚪 CLIENT: Join response status:', response.status);

      if (response.ok) {
        const joinData = await response.json();
        console.log('🎉 CLIENT: Successfully joined consultation:', joinData);
        setStep('ready');
        
        // Construct proper URL with all required parameters instead of relying on backend joinUrl
        const orgId = organizationId || 'org_1754742629431_tj1rxx'; // Use provided org ID or default
        // Use roomId from joinData response, fall back to consultationStatus if needed
        const roomId = joinData.roomId || joinData.meetingId || consultationStatus?.meetingId;
        const requestId = joinData.requestId || consultationStatus?.requestId || reqId;
        const clientName = encodeURIComponent(clientInfo.name);
        
        console.log('🔧 CLIENT: Using consultation data:', {
          roomId,
          requestId, 
          clientName: clientInfo.name,
          joinData,
          consultationStatus
        });
        
        if (!roomId) {
          console.error('🔴 CLIENT: No roomId available from joinData or consultationStatus');
          console.error('🔴 CLIENT: joinData:', joinData);
          console.error('🔴 CLIENT: consultationStatus:', consultationStatus);
          return;
        }
        
        const properUrl = `${window.location.origin}/${orgId}/templates/legal/session/participant?roomId=${roomId}&clientName=${clientName}&requestId=${requestId}&participantType=customer`;
        
        console.log('🔗 CLIENT: Redirecting to consultation room with proper parameters:', properUrl);
        console.log('🔗 CLIENT: Original backend URL was:', joinData.joinUrl);
        window.location.href = properUrl;
      } else {
        const errorText = await response.text();
        console.error('🔴 CLIENT: Failed to join consultation:', errorText);
      }
    } catch (err) {
      console.error('🔴 CLIENT: Error joining consultation:', err);
    }
  };

  const renderIntakeForm = () => (
    <div className="max-w-lg mx-auto">
      <BrandedHeader size="large" />

      <BrandedCard padding="large">
        <form onSubmit={(e) => { e.preventDefault(); submitConsultationRequest(); }}>
          <BrandedInput
            label="Full Name *"
            type="text"
            value={clientInfo.name}
            onChange={(e) => setClientInfo({...clientInfo, name: e.target.value})}
            placeholder="Enter your full name"
            required
          />

          <BrandedInput
            label="Email Address *"
            type="email"
            value={clientInfo.email}
            onChange={(e) => setClientInfo({...clientInfo, email: e.target.value})}
            placeholder="your.email@example.com"
            required
          />

          <BrandedInput
            label="Phone Number *"
            type="tel"
            value={clientInfo.phone}
            onChange={(e) => setClientInfo({...clientInfo, phone: e.target.value})}
            placeholder="+1 (555) 123-4567"
            required
          />

          <BrandedSelect
            label="Type of Legal Matter *"
            value={clientInfo.caseType}
            onChange={(e) => setClientInfo({...clientInfo, caseType: e.target.value})}
            required
            options={[
              { value: '', label: 'Select case type' },
              { value: 'personal_injury', label: 'Personal Injury' },
              { value: 'criminal_defense', label: 'Criminal Defense' },
              { value: 'family_law', label: 'Family Law' },
              { value: 'contract_dispute', label: 'Contract Dispute' },
              { value: 'employment_law', label: 'Employment Law' },
              { value: 'estate_planning', label: 'Estate Planning' },
              { value: 'business_law', label: 'Business Law' },
              { value: 'immigration', label: 'Immigration' },
              { value: 'other', label: 'Other' },
            ]}
          />

          <BrandedSelect
            label="Urgency Level"
            value={clientInfo.urgency}
            onChange={(e) => setClientInfo({...clientInfo, urgency: e.target.value as 'high' | 'medium' | 'low'})}
            options={[
              { value: 'low', label: 'Low - General consultation' },
              { value: 'medium', label: 'Medium - Time-sensitive matter' },
              { value: 'high', label: 'High - Urgent legal issue' },
            ]}
          />

          <BrandedTextarea
            label="Brief Description of Your Case *"
            value={clientInfo.description}
            onChange={(e) => setClientInfo({...clientInfo, description: e.target.value})}
            placeholder="Please provide a brief description of your legal matter..."
            rows={4}
            required
          />

          <BrandedSelect
            label="Preferred Language"
            value={clientInfo.preferredLanguage}
            onChange={(e) => setClientInfo({...clientInfo, preferredLanguage: e.target.value})}
            options={[
              { value: 'en', label: 'English' },
              { value: 'es', label: 'Spanish' },
              { value: 'fr', label: 'French' },
              { value: 'other', label: 'Other' },
            ]}
          />

          {error && (
            <BrandedAlert variant="error" className="mb-6">
              {error}
            </BrandedAlert>
          )}

          <BrandedButton
            type="submit"
            disabled={isSubmitting}
            size="large"
            className="w-full"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center">
                <Loader className="animate-spin mr-2" size={20} />
                Submitting Request...
              </span>
            ) : (
              'Request Consultation'
            )}
          </BrandedButton>

          <div className="mt-6 text-xs text-center" style={{ color: 'var(--brand-text-muted)' }}>
            <p className="mb-2">
              <strong>Confidentiality Notice:</strong> Your consultation will be protected by attorney-client privilege.
            </p>
            <p>
              By submitting this form, you acknowledge that no attorney-client relationship is formed until 
              we formally agree to represent you.
            </p>
          </div>
        </form>
      </BrandedCard>
    </div>
  );

  const renderWaitingRoom = () => (
    <div className="max-w-2xl mx-auto text-center">
      <BrandedHeader size="large" showTagline={false} />
      <p className="mb-8" style={{ color: 'var(--brand-text-secondary)' }}>Your consultation request has been submitted</p>

      <BrandedCard padding="large" className="mb-6">
        <div className="flex items-center justify-center mb-6">
          <div className="animate-pulse rounded-full p-4" style={{ backgroundColor: 'var(--brand-primary)', opacity: 0.1 }}>
            <Clock className="w-8 h-8" style={{ color: 'var(--brand-primary)' }} />
          </div>
        </div>

        <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--brand-text-primary)' }}>
          Waiting for Attorney to Accept
        </h2>

        {consultationStatus && (
          <div className="space-y-4">
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--brand-background)' }}>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span style={{ color: 'var(--brand-text-muted)' }}>Request ID:</span>
                  <p className="font-mono" style={{ color: 'var(--brand-text-primary)' }}>{requestId?.split('_').pop()}</p>
                </div>
                <div>
                  <span style={{ color: 'var(--brand-text-muted)' }}>Case Type:</span>
                  <p className="capitalize" style={{ color: 'var(--brand-text-primary)' }}>{consultationStatus.caseType.replace(/_/g, ' ')}</p>
                </div>
                <div>
                  <span style={{ color: 'var(--brand-text-muted)' }}>Urgency:</span>
                  <BrandedBadge variant={consultationStatus.urgency === 'high' ? 'error' : consultationStatus.urgency === 'medium' ? 'warning' : 'success'}>
                    {consultationStatus.urgency.toUpperCase()}
                  </BrandedBadge>
                </div>
                <div>
                  <span style={{ color: 'var(--brand-text-muted)' }}>Status:</span>
                  <BrandedBadge variant="primary">
                    {consultationStatus.status.toUpperCase()}
                  </BrandedBadge>
                </div>
              </div>
              
              {/* Show Room ID for debugging */}
              {(consultationStatus.meetingId || consultationStatus.roomId) && (
                <BrandedAlert variant="info" className="mt-4">
                  <p className="text-sm font-semibold mb-1">Debug Info - Meeting Room ID:</p>
                  <p className="font-mono text-xs">{consultationStatus.meetingId || consultationStatus.roomId}</p>
                  <p className="text-xs mt-2">Use this ID to join from the test page at /templates/legal/join</p>
                </BrandedAlert>
              )}
            </div>

            {consultationStatus.queuePosition && (
              <p className="text-gray-600">
                Queue Position: <span className="font-semibold">{consultationStatus.queuePosition}</span>
              </p>
            )}

            {consultationStatus.estimatedWait && (
              <p className="text-gray-600">
                Estimated Wait: <span className="font-semibold">{consultationStatus.estimatedWait}</span>
              </p>
            )}
          </div>
        )}

        <p className="text-gray-600 mt-6">
          You will automatically be redirected when the attorney is ready to meet with you.
        </p>
      </BrandedCard>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">What happens next?</h3>
        <ol className="text-sm text-blue-800 text-left space-y-1">
          <li>1. Your attorney will review your request</li>
          <li>2. When ready, they'll accept your consultation</li>
          <li>3. A video room will be created automatically</li>
          <li>4. You'll be redirected to join the consultation</li>
        </ol>
      </div>
    </div>
  );

  const renderReadyState = () => (
    <div className="max-w-2xl mx-auto text-center">
      <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Consultation Ready!</h1>
      <p className="text-gray-600 mb-8">Your attorney has accepted your request</p>

      <div className="bg-white rounded-xl shadow-lg p-8">
        <Video className="w-12 h-12 text-blue-600 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Redirecting to Video Consultation...
        </h2>
        <p className="text-gray-600">
          Please wait while we connect you to your attorney.
        </p>
      </div>
    </div>
  );

  const renderErrorState = () => (
    <div className="max-w-2xl mx-auto text-center">
      <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Request Failed</h1>
      <p className="text-gray-600 mb-8">There was an error submitting your consultation request</p>

      <div className="bg-white rounded-xl shadow-lg p-8">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        <button
          onClick={() => { setStep('intake'); setError(null); }}
          className="bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen py-12 px-4" style={{ backgroundColor: 'var(--brand-background)' }}>
      {step === 'intake' && renderIntakeForm()}
      {step === 'waiting' && renderWaitingRoom()}
      {step === 'ready' && renderReadyState()}
      {step === 'error' && renderErrorState()}
    </div>
  );
};